import React, { type FC, memo } from 'react';
import { connect, type MapStateToProps } from 'react-redux';
import { useLocation } from 'react-router';
import { compose } from '@reduxjs/toolkit';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import type { SportId } from 'app/lib/betting/constants';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { Skeleton, StageTable } from 'app/modules/Betting';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

import { entityLoading, entityRequested } from '../../selectors/gridEntities';
import Tournaments from './Tournaments';
import { Unions, UnionsVirtualizer } from './Unions';

import styles from './Sport.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type StoreProps = {
  requested: boolean;
  loading: boolean;
  tournamentUnionId: number;
  tournamentId: number;
  sportId: number;
};

type SportProps = {
  sportId: SportId;
};

const SportWithoutHOC: FC<SportProps & StoreProps> = memo(({
  requested,
  loading,
  tournamentUnionId,
  tournamentId,
  sportId,
}) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const timeFilter = params.get('timeFilter') || 'all';

  const isVirtualizer = useFeatureFlag(FeatureFlag.VIRTUALIZER);

  if (!requested && loading) {
    return (
      <Skeleton />
    );
  }

  const renderGrid = () => {
    if (!tournamentUnionId) {
      if (isVirtualizer) {
        return (
          <UnionsVirtualizer sportId={sportId}
                             timeFilterValue={timeFilter} />
        );
      }

      return (
        <Unions sportId={sportId}
                timeFilterValue={timeFilter} />
      );
    }

    if (!tournamentId) {
      return <Tournaments timeFilterValue={timeFilter} />;
    }

    return <StageTable timeFilterValue={timeFilter} />;
  };


  return (
    <div className={styles.scrollContainer}>
      <ErrorBoundary>
        {renderGrid()}
      </ErrorBoundary>
    </div>
  );
});

const mapStateToProps: MapStateToProps<
StoreProps,
  BettingContextProps,
  ReduxState
  > = (state: ReduxState, {
    $betting: {
      navigation: {
        selectors: {
          currentLocation,
        },
      },
    },
  }) => {
    const {
      tournamentUnionId,
      tournamentId,
      sportId,
    } = currentLocation(state);

    return {
      requested:  entityRequested(state),
      loading:    entityLoading(state),
      sportId,
      tournamentUnionId,
      tournamentId,
    };
  };

export const Sport = compose<FC<SportProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(SportWithoutHOC);
